import type { LayoutLink, Relationship, RelationTypeChild } from '../types.ts';

/** Minimale vorm die de predicaten hieronder nodig hebben. */
type RelLike =
  Pick<Relationship, 'type' | 'relationSubtype'> | { type?: string; relationSubtype?: string };

/**
 * Relatie-types die een OUDER-KIND-band uitdrukken (personA = ouder, personB = kind).
 * Let op: dit zijn CLIENT-waarden — relTypeFromServer() mapt de DB-vorm ('parent',
 * 'adoptive_parent', 'foster_parent') hiernaartoe. 'parent' staat er defensief bij.
 */
const PARENT_TYPES = new Set([
  'parent-child',
  'adoptive',
  'foster',
  'parent',
  // Defensief: relationshipFromServer mapt bekende DB-types, maar laat een
  // onbekend type ('guardian') rauw door.
  'adoptive_parent',
  'foster_parent',
  'guardian',
]);

/**
 * Types/subtypes waarbij het kind NIET van deze ouder afstamt.
 * 'suspected'/'acknowledged' zijn wél (vermoede) bloedbanden en horen er dus NIET in.
 */
const NON_BIOLOGICAL = new Set([
  'adoptive',
  'foster',
  'step',
  'adoptive_parent',
  'foster_parent',
  'guardian',
]);

/** Legacy: 'adoptive'/'foster' als TYPE is de oude vorm van relationSubtype (pre-#1089). */
const LEGACY_TYPE_TO_SUBTYPE: Record<string, string> = {
  adoptive: 'adoptive',
  adoptive_parent: 'adoptive',
  foster: 'foster',
  foster_parent: 'foster',
};

/**
 * Het effectieve afstammings-subtype van een ouder-kind-rel. Adoptie/pleeg kan op
 * TWEE manieren zijn vastgelegd: de PersonEditor schrijft het als `type` ('adoptive'),
 * de GEDCOM-import als `relationSubtype` op een gewone 'parent-child'-rel. Kijk dus
 * naar beide, anders exporteert een geïmporteerd adoptiekind als biologisch.
 */
export const effectiveChildSubtype = (rel: RelLike): RelationTypeChild | undefined =>
  (rel.relationSubtype ?? LEGACY_TYPE_TO_SUBTYPE[rel.type ?? '']) as RelationTypeChild | undefined;

/**
 * Elke ouder-kind-band — biologisch, adoptief, pleeg of stief.
 * Voor tree-, gezins- en afstammingsweergaves: die tonen het gezin zoals het was,
 * met het type als lijn-stijl (spec §9: stippellijn = adoptie/pleeg/stief).
 */
export const isParentRel = (rel: RelLike): boolean => PARENT_TYPES.has(rel.type ?? '');

/**
 * Alleen bloedverwantschap. Voor Sosa/kwartierstaat/waaier: Sosa-Stradonitz IS
 * per definitie afstamming, en de server nummert ook uitsluitend type='parent'
 * (repos/projects.js fetchParentRelationships). Zonder de isParentRel-guard zou
 * een 'spousal'-rel als ouder tellen — personB is daar de partner, niet het kind.
 */
export const isBiologicalParentRel = (rel: RelLike): boolean =>
  isParentRel(rel) && !NON_BIOLOGICAL.has(effectiveChildSubtype(rel) ?? rel.type ?? '');

/**
 * Map een relatie-type of subtype naar de GEDCOM-PEDI lijn-styling-waarde
 * op een parent-child LayoutLink. Accepteert zowel het coarse RelType als de
 * rijke RelationTypeChild-subtypes (step/suspected/acknowledged zijn nu ook
 * gedekt). 'biological' en alle onbekende/biologische types → undefined
 * zodat de renderer een kale (solide) conn-line tekent.
 */
export function resolvePediType(rel: string | undefined): LayoutLink['pediType'] | undefined {
  switch (rel) {
    case 'adoptive':
      return 'adopted';
    case 'foster':
      return 'foster';
    case 'step':
      return 'step';
    case 'suspected':
      return 'suspected';
    case 'acknowledged':
      return 'acknowledged';
    default:
      return undefined; // biological / parent-child / parent → solide lijn
  }
}
